/*
* <license header>
*/

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  View,
  Flex,
  Heading,
  Text,
  TextField,
  Button,
  Form
} from '@adobe/react-spectrum'
import PlayCircle from '@spectrum-icons/workflow/PlayCircle'
import { PageHeaderBanner } from './shared/PageHeaderBanner'
import { APP_TYPES } from '../data/constants'

const CREATING_SECTION_MIN_HEIGHT = 400

const SECTION_STYLE = {
  border: '1px solid var(--card-border)',
  borderRadius: '8px',
  padding: '1rem',
  backgroundColor: 'var(--white)'
}

const SECTION_STYLE_READONLY = {
  ...SECTION_STYLE,
  backgroundColor: 'var(--gray-75)',
  opacity: 0.9
}

const validateAppName = (value) => {
  const trimmed = (value || '').trim()
  if (!trimmed) return 'App name is required'
  if (trimmed.length < 2) return 'App name must be at least 2 characters'
  return null
}

const validateGithubRepo = (value) => {
  const trimmed = (value || '').trim()
  if (!trimmed) return 'GitHub repository name is required'
  if (!/^[a-zA-Z0-9_.-]+(\/[a-zA-Z0-9_.-]+)?$/.test(trimmed)) {
    return 'Use format: owner/repo or repo-name'
  }
  return null
}

const validateAppType = (value) => {
  if (!value) return 'Please select an application type'
  return null
}

const CARD_BASE = {
  border: '1px solid var(--card-border)',
  borderRadius: '8px',
  padding: '0.75rem 1rem',
  cursor: 'pointer',
  minWidth: 140,
  width: 180,
  minHeight: 150,
  height: 150,
  flex: '0 0 180px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}

export const OnboardExistingApp = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [appName, setAppName] = useState('')
  const [githubRepo, setGithubRepo] = useState('')
  const [appType, setAppType] = useState('')
  const [errors, setErrors] = useState({})
  const [isCreating, setIsCreating] = useState(false)

  const isStep1ReadOnly = step > 1
  const isStep2Visible = step >= 2
  const isStep2ReadOnly = step > 2
  const isStep3Visible = step >= 3
  const isStep3ReadOnly = step > 3

  const handleNext = () => {
    if (step === 1) {
      const err = validateAppName(appName)
      if (err) {
        setErrors({ appName: err })
        return
      }
      setErrors({})
      setStep(2)
    } else if (step === 2) {
      const err = validateGithubRepo(githubRepo)
      if (err) {
        setErrors({ githubRepo: err })
        return
      }
      setErrors({})
      setStep(3)
    } else if (step === 3) {
      const err = validateAppType(appType)
      if (err) {
        setErrors({ appType: err })
        return
      }
      setErrors({})
      setStep(4)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setErrors({})
      setStep(1)
    } else if (step === 3) {
      setErrors({})
      setStep(2)
    } else if (step === 4) {
      setErrors({})
      setStep(3)
    }
  }

  const handleConfirm = () => {
    setIsCreating(true)
  }

  useEffect(() => {
    if (!isCreating) return
    const timer = setTimeout(() => {
      navigate('/apps/1')
    }, 3000)
    return () => clearTimeout(timer)
  }, [isCreating, navigate])

  const selectedAppType = APP_TYPES.find((t) => t.slug === appType)

  return (
    <View
      width="100%"
      height="100%"
      padding="size-400"
      UNSAFE_style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        paddingBottom: '2.5rem'
      }}
    >
      <PageHeaderBanner
        title="Onboard existing application"
        subtitle="Add an existing App Builder application to the factory"
      />

      <View
        flex={1}
        UNSAFE_style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '1.5rem',
          overflow: 'auto',
          minWidth: 0
        }}
      >
        <Flex direction="column" gap="size-300">
        {/* Section 1: App name */}
        <View
          UNSAFE_style={isStep1ReadOnly ? SECTION_STYLE_READONLY : SECTION_STYLE}
        >
          <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem' }}>
            What is the name you want displayed in App Builder?
          </Heading>
          <Form necessityIndicator="label" UNSAFE_style={{ width: '100%' }}>
            <TextField
              label="App name"
              placeholder="e.g. My Awesome App"
              value={appName}
              onChange={setAppName}
              isDisabled={isStep1ReadOnly}
              width="100%"
              validationState={errors.appName ? 'invalid' : undefined}
              errorMessage={errors.appName}
              UNSAFE_style={isStep1ReadOnly ? { opacity: 0.8 } : undefined}
            />
            {!isStep1ReadOnly && (
              <Flex marginTop="size-300" justifyContent="end">
                <Button variant="accent" onPress={handleNext}>
                  Next
                </Button>
              </Flex>
            )}
          </Form>
        </View>

        {/* Section 2: GitHub repository */}
        {isStep2Visible && (
          <View
            UNSAFE_style={isStep2ReadOnly ? SECTION_STYLE_READONLY : SECTION_STYLE}
          >
            <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem' }}>
              GitHub repository name
            </Heading>
            <Form necessityIndicator="label" UNSAFE_style={{ width: '100%' }}>
              <TextField
                label="Repository"
                placeholder="e.g. owner/repo-name or repo-name"
                value={githubRepo}
                onChange={setGithubRepo}
                isDisabled={isStep2ReadOnly}
                width="100%"
                validationState={errors.githubRepo ? 'invalid' : undefined}
                errorMessage={errors.githubRepo}
                UNSAFE_style={isStep2ReadOnly ? { opacity: 0.8 } : undefined}
              />
              <Flex marginTop="size-300" gap="size-200" justifyContent="end">
                <Button variant="secondary" onPress={handleBack}>
                  Back
                </Button>
                {!isStep2ReadOnly && (
                  <Button variant="accent" onPress={handleNext}>
                    Next
                  </Button>
                )}
              </Flex>
            </Form>
          </View>
        )}

        {/* Section 3: Application type */}
        {isStep3Visible && (
          <View
            UNSAFE_style={isStep3ReadOnly ? SECTION_STYLE_READONLY : SECTION_STYLE}
          >
            <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem' }}>
              What type of application is it?
            </Heading>
            <Flex gap="size-200" wrap marginTop="size-200">
              {APP_TYPES.map((type) => {
                const isSelected = appType === type.slug
                return (
                  <div
                    key={type.slug}
                    role="button"
                    tabIndex={isStep3ReadOnly ? -1 : 0}
                    aria-pressed={isSelected}
                    aria-label={`Select ${type.title}`}
                    onKeyDown={(e) => {
                      if (!isStep3ReadOnly && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        setAppType(type.slug)
                      }
                    }}
                    onClick={() => !isStep3ReadOnly && setAppType(type.slug)}
                    style={{
                      ...CARD_BASE,
                      borderColor: isSelected ? 'var(--spectrum-global-color-blue-600)' : 'var(--card-border)',
                      backgroundColor: isSelected ? 'rgba(2, 101, 220, 0.08)' : 'var(--white)',
                      pointerEvents: isStep3ReadOnly ? 'none' : 'auto',
                      opacity: isStep3ReadOnly ? 0.8 : 1
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', flexShrink: 0 }}>
                      {type.title}
                    </span>
                    <span
                      style={{
                        color: 'var(--gray-600)',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flex: 1,
                        minHeight: 0
                      }}
                    >
                      {type.description}
                    </span>
                  </div>
                )
              })}
            </Flex>
            {errors.appType && (
              <Text UNSAFE_style={{ color: 'var(--spectrum-semantic-negative-color-default)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                {errors.appType}
              </Text>
            )}
            <Flex marginTop="size-300" gap="size-200" justifyContent="end">
              <Button variant="secondary" onPress={handleBack}>
                Back
              </Button>
              {!isStep3ReadOnly && (
                <Button variant="accent" onPress={handleNext}>
                  Next
                </Button>
              )}
            </Flex>
          </View>
        )}

        {/* Section 4: Review and confirm */}
        {step >= 4 && (
          <View UNSAFE_style={SECTION_STYLE}>
            {isCreating ? (
              <View
                UNSAFE_style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: CREATING_SECTION_MIN_HEIGHT
                }}
              >
                <Flex direction="column" alignItems="center" gap="size-200">
                  <PlayCircle size="XL" UNSAFE_style={{ color: 'var(--card-accent)' }} />
                  <Text UNSAFE_style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                    Your app is being created...
                  </Text>
                  <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                    Redirecting you to your app in a moment.
                  </Text>
                </Flex>
              </View>
            ) : (
              <>
                <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem' }}>
                  Review and confirm
                </Heading>
                <Text UNSAFE_style={{ marginBottom: '1rem', color: 'var(--gray-700)' }}>
                  Please review the values below before your app gets added. Once confirmed, your app will be created.
                </Text>
                <View
                  UNSAFE_style={{
                    padding: '1rem',
                    backgroundColor: 'var(--gray-75)',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    overflow: 'auto'
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>App name</td>
                        <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{appName}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>Repository</td>
                        <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{githubRepo}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>Application type</td>
                        <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{selectedAppType?.title ?? appType}</td>
                      </tr>
                    </tbody>
                  </table>
                </View>
                <Flex gap="size-200" justifyContent="end">
                  <Button variant="secondary" onPress={handleBack}>
                    Back
                  </Button>
                  <Button variant="accent" onPress={handleConfirm}>
                    Confirm and create app
                  </Button>
                </Flex>
              </>
            )}
          </View>
        )}
        </Flex>
      </View>
    </View>
  )
}
